import { useEffect, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { messageKeys } from './useMessages'
import type { NewMessageEvent } from '../types/message'

interface UseMessageSSEOptions {
  enabled?: boolean
  onNewMessage?: (event: NewMessageEvent) => void
}

/**
 * SSE를 통한 실시간 메시지 알림 구독
 */
export function useMessageSSE(options: UseMessageSSEOptions = {}) {
  const { enabled = true, onNewMessage } = options
  const queryClient = useQueryClient()
  const eventSourceRef = useRef<EventSource | null>(null)

  const handleNewMessage = useCallback((event: MessageEvent) => {
    try {
      const data: NewMessageEvent = JSON.parse(event.data)

      // unreadCount 캐시 무효화
      queryClient.invalidateQueries({ queryKey: messageKeys.unreadCount() })

      // 메시지 목록 캐시 무효화
      queryClient.invalidateQueries({ queryKey: messageKeys.all })

      // 콜백 호출
      onNewMessage?.(data)
    } catch (e) {
      console.error('Failed to parse SSE event:', e)
    }
  }, [queryClient, onNewMessage])

  useEffect(() => {
    if (!enabled) return

    // 토큰 가져오기
    let token = ''
    try {
      const authStorage = localStorage.getItem('auth-storage')
      if (authStorage) {
        const parsed = JSON.parse(authStorage)
        const state = parsed?.state || parsed
        token = state?.accessToken || ''
      }
    } catch (e) {
      console.error('Failed to get token for SSE:', e)
    }

    if (!token) {
      console.debug('No token available for SSE connection')
      return
    }

    // SSE는 절대 URL이 필요. Vercel은 /api를 rewrite하므로 origin + /api 사용
    const baseUrl = import.meta.env.VITE_API_BASE_URL || `${window.location.origin}/api`
    const eventSource = new EventSource(`${baseUrl}/my/messages/subscribe?token=${encodeURIComponent(token)}`, {
      withCredentials: true
    })

    eventSource.addEventListener('new-message', handleNewMessage)

    eventSource.addEventListener('connected', () => {
      console.debug('SSE connected')
    })

    eventSource.onerror = (error) => {
      console.debug('SSE error, will auto-reconnect:', error)
    }

    eventSourceRef.current = eventSource

    return () => {
      eventSource.close()
      eventSourceRef.current = null
    }
  }, [enabled, handleNewMessage])

  return {
    isConnected: !!eventSourceRef.current
  }
}
